import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip'
import { useProgram } from '@/hooks/useProgram'
import { useProgramContext } from '@/context/ProgramContext'
import {
	ChevronUp,
	ChevronDown,
	PlusCircle,
	Loader2,
	Crown,
	AlertCircle,
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { useToast } from '@/components/ui/use-toast'
import { useTranslation } from '@/hooks/useTranslation'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
	DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useFeatureLimit } from '@/hooks/useFeatureLimit'
import { Badge } from '@/components/ui/badge'
import { getPlanDefinition } from '@/lib/entitlements'
import { supportsOverageBilling } from '@/lib/planHelpers'

interface ProgramSelectorProps {
	isCollapsed?: boolean
}

function ProgramMark({
	name,
	faviconUrl,
	variant = 'default',
}: {
	name: string
	faviconUrl?: string | null
	variant?: 'default' | 'collapsed' | 'list'
}) {
	const [imgFailed, setImgFailed] = useState(false)
	const trimmed = faviconUrl?.trim()
	const showImg = Boolean(trimmed) && !imgFailed

	useEffect(() => {
		setImgFailed(false)
	}, [trimmed])

	const boxClass =
		variant === 'collapsed'
			? 'h-7 w-7 text-xs'
			: variant === 'list'
				? 'h-7 w-7 text-xs'
				: 'h-8 w-8 text-sm'

	return (
		<div
			className={cn(
				'flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-primary/10 font-medium text-primary',
				boxClass
			)}>
			{showImg ? (
				<img
					src={trimmed}
					alt=""
					className="h-full w-full object-contain p-0.5"
					onError={() => setImgFailed(true)}
					referrerPolicy="no-referrer"
				/>
			) : (
				name?.[0]?.toUpperCase() || 'P'
			)}
		</div>
	)
}

export function ProgramSelector({ isCollapsed = false }: ProgramSelectorProps) {
	const { t } = useTranslation()
	const {
		programs,
		programId,
		setProgramId,
		loading: programsLoading,
		currentProgram,
		createProgram,
	} = useProgram()
	const { isInvitedProgram } = useProgramContext()
	const { toast } = useToast()

	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [programName, setProgramName] = useState('')
	const [creating, setCreating] = useState(false)

	const {
		data: programLimit,
		isLoading: limitLoading,
		error: limitError,
		refetch: refetchProgramLimit,
	} = useFeatureLimit('max_programs')
	const planDefinition = programLimit?.planId
		? getPlanDefinition(programLimit.planId)
		: null
	const includedProjects = planDefinition?.includedProjects ?? 1
	const additionalProjectMonthly =
		planDefinition?.pricing?.additionalProjectMonthly ?? 15
	const isPaidProjectOverage =
		Boolean(programLimit?.planId) &&
		supportsOverageBilling(programLimit?.planId) &&
		!programLimit?.unlimitedProjects &&
		programs.length >= includedProjects &&
		programLimit?.limit === -1
	const shouldBlockProjectCreation = Boolean(
		programLimit && !programLimit.allowed && !isPaidProjectOverage
	)
	const programLimitMessage = shouldBlockProjectCreation
		? programLimit?.message || t('programSelector.upgradeForMore')
		: isPaidProjectOverage
			? programLimit?.message ||
				t('programSelector.additionalProjectChargeNotice', {
					includedProjects,
					price: `$${additionalProjectMonthly}`,
				})
			: null

	const handleValueChange = (value: string) => {
		if (value === 'create-new') {
			setIsDialogOpen(true)
			return
		}
		setProgramId(value)
	}

	const handleCreateProgram = async () => {
		if (!programName.trim()) {
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description: t('programSelector.programNameEmpty'),
			})
			return
		}

		if (limitLoading) {
			toast({
				title: t('programSelector.checkingLimits'),
				description: t('common.loading'),
			})
			return
		}

		if (shouldBlockProjectCreation) {
			toast({
				variant: 'default',
				title: t('programSelector.programLimitReached'),
				description: programLimitMessage || t('programSelector.upgradeForMore'),
			})
			return
		}

		setCreating(true)
		try {
			const newProgram = await createProgram(programName.trim())
			setProgramId(newProgram.id)
			setIsDialogOpen(false)
			setProgramName('')
			toast({
				title: t('common.success'),
				description: t('programSelector.programCreatedSuccess', {
					name: newProgram.name,
				}),
				duration: 5000,
			})
			if (refetchProgramLimit) {
				refetchProgramLimit()
			}
		} catch (error: unknown) {
			toast({
				variant: 'destructive',
				title: t('programSelector.errorCreatingProgram'),
				description:
					error instanceof Error
						? error.message
						: t('programSelector.failedToCreateProgram'),
			})
		} finally {
			setCreating(false)
		}
	}

	if (isCollapsed) {
		return (
			<Select
				value={programId || ''}
				onValueChange={handleValueChange}
				disabled={programsLoading}>
				<SelectTrigger
					className={cn(
						'w-9 h-9 p-0 flex items-center justify-center rounded-md relative',
						'bg-primary/5 text-primary border border-primary/20',
						'hover:bg-primary/10 hover:border-primary/30',
						'transition-all duration-200 ease-in-out',
						'focus:outline-none focus:ring-2 focus:ring-primary/20',
						'[&>svg]:hidden',
						isInvitedProgram && 'border-dashed'
					)}>
					<ProgramMark
						name={currentProgram?.name || ''}
						faviconUrl={currentProgram?.favicon_url}
						variant="collapsed"
					/>
					{isInvitedProgram && (
						<div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-muted-foreground/50 rounded-full" />
					)}
				</SelectTrigger>
				<SelectContent
					className="p-0 min-w-[200px]"
					align="start"
					side="right"
					sideOffset={8}>
					{programsLoading ? (
						<div className="flex items-center justify-center py-4">
							<Loader2 className="h-5 w-5 animate-spin text-primary" />
						</div>
					) : (
						<>
							{programs.map((program) => (
								<SelectItem
									key={program.id}
									value={program.id}
									className={cn(
										'px-3 py-2 text-sm cursor-pointer',
										'hover:bg-primary/10 hover:text-primary',
										'focus:bg-primary/10 focus:text-primary',
										'[&>span:first-child]:hidden'
									)}>
									<div className="flex min-w-0 items-center gap-2">
										<ProgramMark
											name={program.name}
											faviconUrl={program.favicon_url}
											variant="list"
										/>
										<span className="truncate">{program.name}</span>
									</div>
								</SelectItem>
							))}
							{programs.length === 0 && (
								<div className="py-6 text-center text-sm text-muted-foreground">
									{t('programSelector.noProjectAvailable')}
								</div>
							)}

							<div className="h-px bg-border" />
							<SelectItem
								value="create-new"
								className={cn(
									'flex items-center gap-2 mt-2 cursor-pointer rounded-md px-3 py-2 text-sm text-primary whitespace-nowrap',
									'hover:bg-primary/10',
									'focus:bg-primary/10'
								)}>
								<div className="flex items-center gap-2">
									<PlusCircle className="h-4 w-4 shrink-0" />
									{t('programSelector.createNewProject')}
								</div>
							</SelectItem>
						</>
					)}
				</SelectContent>
			</Select>
		)
	}

	return (
		<>
			<Select
				value={programId || ''}
				onValueChange={handleValueChange}
				disabled={programsLoading}>
				<SelectTrigger
					className={cn(
						'w-full h-10 flex items-center justify-between px-3 py-2',
						'bg-background border rounded-lg',
						'hover:bg-accent/50',
						'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
						'disabled:cursor-not-allowed disabled:opacity-50',
						'transition-colors',
						'[&>svg]:hidden'
					)}>
					<div className="flex items-center gap-2 min-w-0">
						<ProgramMark
							name={currentProgram?.name || ''}
							faviconUrl={currentProgram?.favicon_url}
						/>
						<div className="flex items-center gap-2 min-w-0">
							<span className="text-sm truncate">
								{currentProgram?.name || t('programSelector.selectProgram')}
							</span>
							{isInvitedProgram && (
								<Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-normal">
									{t('programSelector.guest')}
								</Badge>
							)}
						</div>
					</div>
					<div className="flex flex-col -space-y-[2px]">
						<ChevronUp className="h-4 w-4 opacity-50" />
						<ChevronDown className="h-4 w-4 opacity-50" />
					</div>
				</SelectTrigger>
				<SelectContent className="p-0 min-w-[200px]" align="start">
					{programsLoading ? (
						<div className="flex items-center justify-center py-4">
							<Loader2 className="h-5 w-5 animate-spin text-primary" />
						</div>
					) : (
						<>
							{programs.map((program) => (
								<SelectItem
									key={program.id}
									value={program.id}
									className={cn(
										'px-3 py-2 text-sm cursor-pointer',
										'hover:bg-primary/10 hover:text-primary',
										'focus:bg-primary/10 focus:text-primary',
										'[&>span:first-child]:hidden'
									)}>
									<div className="flex min-w-0 items-center gap-2">
										<ProgramMark
											name={program.name}
											faviconUrl={program.favicon_url}
											variant="list"
										/>
										<span className="truncate">{program.name}</span>
									</div>
								</SelectItem>
							))}
							{programs.length === 0 && (
								<div className="py-6 text-center text-sm text-muted-foreground">
									{t('programSelector.noProjectAvailable')}
								</div>
							)}
							<div className="h-px bg-border" />
							<SelectItem
								value="create-new"
								className={cn(
									'flex items-center gap-2 mt-2 cursor-pointer rounded-md px-3 py-2 text-sm text-primary whitespace-nowrap',
									'hover:bg-primary/10',
									'focus:bg-primary/10'
								)}>
								<div className="flex items-center gap-2">
									<PlusCircle className="h-4 w-4 shrink-0" />
									{t('programSelector.createNewProject')}
								</div>
							</SelectItem>
						</>
					)}
				</SelectContent>
			</Select>

			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('programSelector.createNewProjectTitle')}</DialogTitle>
						<DialogDescription>
							{t('programSelector.createNewProjectDescription')}
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-4">
						<div className="space-y-2">
							<Label htmlFor="new-program-name">{t('programSelector.projectName')}</Label>
							<Input
								id="new-program-name"
								placeholder={t('programSelector.projectNamePlaceholder')}
								value={programName}
								onChange={(e) => setProgramName(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleCreateProgram()
								}}
							/>
						</div>

						{limitLoading && (
							<div className="flex items-center text-sm text-muted-foreground">
								<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('programSelector.checkingLimits')}
							</div>
						)}
						{limitError && (
							<Alert variant="destructive" className="mt-2">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>{limitError.message}</AlertDescription>
							</Alert>
						)}
						{programLimitMessage && (
							<Alert
								variant={shouldBlockProjectCreation ? 'destructive' : 'default'}
								className="mt-2">
								<AlertCircle className="h-4 w-4" />
								<AlertDescription>
									{programLimitMessage}
									{shouldBlockProjectCreation && (
										<>
											{' '}
											<a
												href="/settings?tab=billing"
												className="font-medium underline underline-offset-2">
												{t('programSelector.upgradePlan')}
											</a>
										</>
									)}
								</AlertDescription>
							</Alert>
						)}
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsDialogOpen(false)}
							disabled={creating}>
							{t('common.cancel')}
						</Button>
						<Button
							onClick={handleCreateProgram}
							disabled={
								creating ||
								!programName.trim() ||
								limitLoading ||
								shouldBlockProjectCreation
							}>
							{creating ? (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							) : (
								<PlusCircle className="mr-2 h-4 w-4" />
							)}
							{t('programSelector.createProject')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	)
}
